import React from "react";
import { createPortal } from "react-dom";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

function DownloadIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 3v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M4 20h16" />
    </svg>
  );
}

function CloseIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function slugify(texto) {
  return String(texto || "invitacion")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "invitacion";
}

/**
 * Modal de previsualización de la invitación generada.
 * @param {{ open: boolean, dataUrl: string, nombre: string, onClose: () => void }} props
 */
export default function InvitationPreviewModal({ open, dataUrl, nombre, onClose }) {
  useLockBodyScroll(open);

  if (!open || !dataUrl) return null;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `invitacion-${slugify(nombre)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const modal = (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Previsualización de la invitación"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 bg-gray-900 border border-primary/30 rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto p-5 sm:p-6 animate-fade-in">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition p-1 rounded-lg hover:bg-gray-800"
          aria-label="Cerrar previsualización"
        >
          <CloseIcon className="w-5 h-5" />
        </button>

        <h3 className="font-bold text-lg text-white mb-1 pr-8">Invitación generada</h3>
        <p className="text-gray-400 text-sm mb-4">
          Previsualización de la invitación de <span className="text-primary font-semibold">{nombre}</span>.
        </p>

        <div className="rounded-xl overflow-hidden bg-black/40 border border-gray-700 flex items-center justify-center">
          <img
            src={dataUrl}
            alt={`Invitación de ${nombre}`}
            className="w-full h-auto max-h-[62vh] object-contain"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-5">
          <button
            type="button"
            onClick={handleDownload}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-white font-bold px-4 py-2.5 rounded-lg shadow hover:brightness-110 transition"
          >
            <DownloadIcon className="w-5 h-5" />
            Descargar PNG
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-500 text-gray-200 px-4 py-2.5 rounded-lg hover:bg-gray-800 transition"
          >
            <CloseIcon className="w-5 h-5" />
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
